import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { User, Mail, Lock, Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import { authAPI } from "../../api/authApi";
import styles from "./Register.module.css";
import logo from './logo.jpg';

function RegisterPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ name: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await authAPI.register_client(data);
      const loginResponse = await authAPI.login_client({
        email: data.email,
        password: data.password
      });

      if (loginResponse.token) {
        localStorage.setItem('token', loginResponse.token);
        navigate('/');
      }
    } catch (err) {
      setError('Não foi possível criar a conta. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Orbes de luz decorativos no fundo */}
      <div className={styles.blobBlue} />
      <div className={styles.blobGold} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className={styles.card}
      >
        {/* Destaque para a Logo */}
        <div className={styles.logoWrapper}>
          <img src={logo} alt="Logo" className={styles.logoImage} />
        </div>

        <header className={styles.header}>
          <h1 className={styles.title}>Crie sua conta</h1>
          <p className={styles.subtitle}>Preencha os dados para começar.</p>
        </header>

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Nome */}
          <div className={styles.field}>
            <label>Nome Completo</label>
            <div className={styles.inputWrapper}>
              <User className={styles.icon} size={18} />
              <input
                type="text"
                name="name"
                placeholder="Ex: João Silva"
                value={data.name}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* E-mail */}
          <div className={styles.field}>
            <label>E-mail</label>
            <div className={styles.inputWrapper}>
              <Mail className={styles.icon} size={18} />
              <input
                type="email"
                name="email"
                placeholder="seu@email.com"
                value={data.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Senha */}
          <div className={styles.field}>
            <label>Senha</label>
            <div className={styles.inputWrapper}>
              <Lock className={styles.icon} size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="••••••••"
                value={data.password}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className={styles.eyeBtn}
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Mensagem de Erro */}
          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0 }}
                className={styles.error}
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Botão de Cadastro */}
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            type="submit"
            className={styles.submitBtn}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className={styles.spinner} size={20} />
            ) : (
              <>
                <span>Criar Conta</span>
                <ArrowRight size={18} />
              </>
            )}
          </motion.button>

          <Link to="/login" className={styles.footerLink}>
            Já possui uma conta? <span>Entrar</span>
          </Link>
        </form>
      </motion.div>
    </div>
  );
}

export default RegisterPage;